import angular from 'angular';

import * as mediaActions from '../../../store/actions/media';

import template from './media-viewer.html';

import './media-viewer.less';

const mapStateToScope = (state) => {
  return {
    media: state.media,
  };
};

/*
 *  Media Viewer component to display set of media items in a gallery/carousel
 *  @param {array} staticMedia (optional) - array of media objects for display
 *      if not provided will use media.activeMedia from state
 *  @param {boolean} supperErrors - set to true if error messages should be suppressed.
 *  @param {number} numberThumbnails - number of thumbnails to display in the slider
 */

angular.module('myConsultApp').directive('mediaViewer', () => {
  return {
    scope: {
      staticMedia: '=',
      suppressErrors: '@',
      numberThumbnails: '@',
    },
    controller: ($scope, $ngRedux, $http, ENV, $timeout) => {
      $scope.API = null;
      $scope.videoPlayerConfig = {
        preload: 'none',
        sources: [],
      };
      $scope.slides = []; // array of media objects to load into slider
      $scope.displayedMedia = {}; // media item displayed in primary gallery screen (from $scope.slides)
      $scope.sliderPosition = 0; // initial slider position
      $scope.slideWrapperWidth = 0;

      $scope.init = () => {
        $scope.unsubscribe = $ngRedux.connect(mapStateToScope, mediaActions)($scope);
      };
      $scope.init();

      // initialize videogular API
      $scope.onPlayerReady = (API) => {
        $scope.API = API;
      };

      $scope.isVideo = (media) => {
        return media.type === 'video' || media.type === 'video-mp4';
      };

      $scope.isImage = (media) => {
        return media.type === 'image';
      };

      $scope.isFile = (media) => {
        return media.type === 'document';
      };

      $scope.loadVideo = (url) => {
        if (!url) {
          return;
        }

        $scope.API?.stop();
        $scope.videoPlayerConfig.sources = [
          {
            src: url,
            type: 'video/mp4',
          },
          {
            src: url,
            type: 'video/quicktime',
          },
        ];
      };

      /*
       *  function: onChangeMedia()
       *  Change active element on gallery screen by assigning the slide to displayedMedia
       *  @param {number} index - index of the slide to set to displayedMedia
       */
      $scope.onChangeMedia = (index) => {
        // do nothing if clicking on same slide
        if ($scope.displayedMedia.index === index) {
          return;
        }
        $scope.displayedMedia = $scope.slides[index];
        // if media type is video load the video
        if ($scope.isVideo($scope.displayedMedia)) {
          $scope.loadVideo($scope.displayedMedia.url);
        }
      };

      /*
       *  function: onSlideChange()
       *  Update carousel slide positioning and gallery screen content on slide change
       *  @param {string} direction - 'next' for forward motion, default 'prev' for backward motion
       */
      $scope.onSlideChange = (direction = 'prev') => {
        const distance = direction === 'next' ? $scope.slideDistance * -1 : $scope.slideDistance;
        const currentPosition = $scope.slider.offsetLeft;
        const lastSlideOffset =
          $scope.sliderPosition >= $scope.numSlides - 2 ? $scope.slideMargin * -1 : 0; // ignore margin on transition to/from last slide
        let newPosition = currentPosition + distance + lastSlideOffset;

        if (
          direction === 'next' &&
          $scope.sliderPosition + 1 === $scope.numSlides - $scope.thumbnails
        ) {
          newPosition = $scope.minPosition;
        } else if (direction === 'prev' && $scope.sliderPosition - 1 === 0) {
          newPosition = $scope.maxPosition;
        }

        if (newPosition >= $scope.minPosition && newPosition <= $scope.maxPosition) {
          $($scope.slider).animate({ left: newPosition });
          $scope.sliderPosition =
            direction === 'next' ? $scope.sliderPosition + 1 : $scope.sliderPosition - 1;
        }
      };

      /*
       *  function: setSlideWrapperWidth()
       *  if slideWrapper width has changed, update it and call $scope.initializeThumbs() to size the thumbnails
       */
      $scope.setSlideWrapperWidth = () => {
        const newWidth = document.querySelector('.media-viewer-gallery-controls-wrapper')
          ?.offsetWidth;
        if ($scope.slideWrapperWidth !== newWidth) {
          $scope.slideWrapperWidth = newWidth;
          $scope.initializeThumbs();
        }
      };

      /*
       *  function: loadMedia()
       *  @param {array} mediaStore - array of media objects to load into viewer
       */
      $scope.loadMedia = (mediaStore) => {
        $scope.isSingleMedia = mediaStore.length === 1;

        $scope.slides = mediaStore.map((item, index) => {
          const type = item.mime_type?.type?.slug;
          return {
            index: index,
            id: item.id,
            thumbnail: item.thumbnail,
            storage_url: item.storage_url,
            tokenized_url: item.tokenized_url,
            type: type,
            status: item.status,
          };
        });
        // get urls for each media object, if we have a tokenized storage_url, use it, otherwise get url from token
        $scope.slides.forEach((slide) => {
          if (slide.storage_url) {
            slide.url = slide.storage_url;
            return;
          }
          if (slide.tokenized_url) {
            $scope.getMediaURL($http, ENV, slide.id).then((res) => {
              slide.url = res.tokenized_url;
            });
          } else {
            slide.url = slide.thumbnail;
          }
        });
        $scope.displayedMedia = $scope.slides[0];
        if ($scope.isVideo($scope.displayedMedia)) {
          $scope.loadVideo($scope.displayedMedia.url);
        }
        $scope.numSlides = $scope.slides.length;
        $scope.thumbnails = $scope.numberThumbnails || $scope.numSlides === 2 ? 2 : 3;

        if (!$scope.isSingleMedia) {
          $timeout($scope.initializeThumbs);
        }
      };

      /*
       *  function: initializeThumbs()
       *  Setup slider scope variables and determine width of thumbnail slides
       */
      $scope.initializeThumbs = () => {
        // disconnect resize observer to ensure they don't pile up
        if ($scope.resize) {
          $scope.resize.disconnect();
        }

        $scope.slider = document.getElementById('media-viewer-gallery-controls-slider');

        if (!$scope.slider) return;

        $scope.slideWrapper = $scope.slider.parentElement;
        $scope.slideWrapperStyle = getComputedStyle($scope.slideWrapper);
        $scope.slideWrapperWidth = parseInt($scope.slideWrapperStyle.width);
        $scope.slideStyle = getComputedStyle($scope.slider.children[0]);
        $scope.slideMargin = parseInt($scope.slideStyle.marginRight);
        $scope.slideWidth =
          (parseInt($scope.slideWrapperStyle.width) -
            4 - // account for border of active thumbnail
            ($scope.thumbnails - 1) * $scope.slideMargin) /
          $scope.thumbnails; // account for thumbnails -1 margins
        $scope.slideDistance = $scope.slideWidth + $scope.slideMargin;
        $scope.minPosition =
          ($scope.numSlides - $scope.thumbnails) * ($scope.slideWidth + $scope.slideMargin) * -1;
        $scope.maxPosition = 0;

        document.querySelectorAll('.media-viewer-gallery-controls-item').forEach((elem) => {
          elem.style.width = $scope.slideWidth + 'px';
        });

        // observe $scope.slideWrapper to determine if a resize has occurred to set new slideWrapperWidth
        $scope.resize = new ResizeObserver($scope.setSlideWrapperWidth).observe(
          $scope.slideWrapper,
        );
      };

      /*
       * MAIN
       * choose watcher target, either staticMedia if passed, or media.activeMedia from state
       * and load initial media
       */
      const whatToWatch = $scope.staticMedia ? 'staticMedia' : '$scope.media.activeMedia';

      $scope.mediaListener = $scope.$watch(whatToWatch, () => {
        const media = whatToWatch === 'staticMedia' ? $scope.staticMedia : $scope.media.activeMedia;
        if (!media) {
          return;
        }
        $scope.loadMedia(media);
      });

      // cleanup
      $scope.$on('$destroy', () => {
        $scope.unsubscribe();
        $scope.mediaListener();
        if ($scope.resize) {
          $scope.resize.disconnect();
        }
      });
    },
    template,
  };
});
